import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { AdminSidebarLayout } from './SidebarLayout';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import {
  Shield, Plus, Edit, Trash2, Loader2, Save, Users, Lock, Check
} from 'lucide-react';
import { toast } from 'sonner';

export function AdminRolesPage() {
  const { api } = useAuth();
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', modules: [] });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [rolesRes, modulesRes] = await Promise.all([
        api.get('/api/admin/roles'),
        api.get('/api/admin/modules')
      ]);
      setRoles(rolesRes.data.roles || []);
      setModules(modulesRes.data.modules || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const openDialog = (role = null) => {
    if (role) {
      setEditing(role);
      setForm({ name: role.name, description: role.description || '', modules: role.modules || [] });
    } else {
      setEditing(null);
      setForm({ name: '', description: '', modules: [] });
    }
    setDialogOpen(true);
  };

  const toggleModule = (key) => {
    setForm(prev => ({
      ...prev,
      modules: prev.modules.includes(key)
        ? prev.modules.filter(m => m !== key)
        : [...prev.modules, key]
    }));
  };

  const selectAll = () => {
    setForm(prev => ({ ...prev, modules: modules.map(m => m.key) }));
  };

  const clearAll = () => {
    setForm(prev => ({ ...prev, modules: [] }));
  };

  const saveRole = async () => {
    if (!form.name.trim()) { toast.warning('Role name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/api/admin/roles/${editing.id}`, form);
        toast.success('Role updated');
      } else {
        await api.post('/api/admin/roles', form);
        toast.success('Role created');
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save role');
    }
    setSaving(false);
  };

  const deleteRole = async (role) => {
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try {
      await api.delete(`/api/admin/roles/${role.id}`);
      toast.success('Role deleted');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete role');
    }
  };

  if (loading) {
    return (
      <AdminSidebarLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
        </div>
      </AdminSidebarLayout>
    );
  }

  return (
    <AdminSidebarLayout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-roles-page">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8">
          <div>
            <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white">Role Management</h1>
            <p className="text-[#A0B4CC] mt-1">Manage roles and control module access</p>
          </div>
          <Button onClick={() => openDialog()} className="btn-primary" data-testid="add-role-btn">
            <Plus className="w-4 h-4 mr-2" /> New Role
          </Button>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {roles.map(role => (
            <Card key={role.id} className="bg-[#041A3B] border-white/10" data-testid={`role-card-${role.name}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center">
                    {role.is_system ? <Lock className="w-6 h-6 text-[#00E5FF]" /> : <Shield className="w-6 h-6 text-[#00E5FF]" />}
                  </div>
                  <div className="flex gap-1">
                    {role.is_system && <Badge className="bg-[#007AFF]/20 text-[#007AFF] text-xs">System</Badge>}
                  </div>
                </div>
                <h3 className="font-outfit text-lg font-semibold text-white mb-1 capitalize">{role.name}</h3>
                <p className="text-[#5C7599] text-sm mb-4">{role.description}</p>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {(role.modules || []).slice(0, 5).map(m => (
                    <Badge key={m} className="bg-[#00E5FF]/10 text-[#00E5FF] text-xs border-0">{m}</Badge>
                  ))}
                  {(role.modules || []).length > 5 && (
                    <Badge className="bg-[#5C7599]/20 text-[#5C7599] text-xs border-0">
                      +{role.modules.length - 5} more
                    </Badge>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => openDialog(role)} className="flex-1 btn-secondary" data-testid={`edit-role-${role.name}`}>
                    <Edit className="w-4 h-4 mr-1" /> Edit
                  </Button>
                  {!role.is_system && (
                    <Button size="sm" variant="ghost" onClick={() => deleteRole(role)} className="text-[#FF1744]">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Role Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-[#041A3B] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">{editing ? 'Edit Role' : 'Create Role'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white">Role Name</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="bg-[#000B18] border-white/20 text-white"
                    placeholder="e.g. moderator"
                    disabled={editing?.is_system}
                    data-testid="role-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Description</Label>
                  <Input
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="bg-[#000B18] border-white/20 text-white"
                    placeholder="Role description"
                    data-testid="role-description-input"
                  />
                </div>
              </div>

              <div className="border-t border-white/10 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-white font-medium">Module Access</Label>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={selectAll} className="text-[#00E5FF] text-xs h-7">Select All</Button>
                    <Button size="sm" variant="ghost" onClick={clearAll} className="text-[#5C7599] text-xs h-7">Clear</Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {modules.map(m => (
                    <button
                      key={m.key}
                      onClick={() => !(editing?.name === 'admin' && editing?.is_system) && toggleModule(m.key)}
                      className={`flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                        form.modules.includes(m.key)
                          ? 'bg-[#00E5FF]/10 border border-[#00E5FF]/30'
                          : 'bg-[#000B18] border border-white/10 hover:border-white/20'
                      }`}
                      data-testid={`module-toggle-${m.key}`}
                    >
                      <div className={`w-5 h-5 rounded flex items-center justify-center ${
                        form.modules.includes(m.key) ? 'bg-[#00E5FF] text-[#000B18]' : 'bg-[#041A3B] border border-white/20'
                      }`}>
                        {form.modules.includes(m.key) && <Check className="w-3 h-3" />}
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium">{m.label}</p>
                        <p className="text-[#5C7599] text-xs">{m.description}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-[#A0B4CC]">Cancel</Button>
              <Button onClick={saveRole} disabled={saving} className="btn-primary" data-testid="save-role-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminSidebarLayout>
  );
}
